import { Bell, FileText, Gavel, Handshake, Info, UserCheck } from 'lucide-react';
import type { TagColorScheme } from 'pixelize-design-library';
import { createElement, type ReactNode } from 'react';

import type { AppTheme } from '@core/Theme/useAppTheme';
import type { NotificationRow, Section } from '@core/store/Notifications/notificationTypes';

const SECTION_COLOR_SCHEME: Record<Section, TagColorScheme> = {
  buyer: 'purple',
  seller: 'blue',
  broker: 'teal'
};

export function sectionColorScheme(section: Section | null): TagColorScheme | undefined {
  return section ? SECTION_COLOR_SCHEME[section] : undefined;
}

export function iconForType(type: string): ReactNode {
  const kind = type.toLowerCase();
  if (kind.includes('legal_opinion')) return createElement(Gavel, { size: 16 });
  if (kind.includes('document')) return createElement(FileText, { size: 16 });
  if (kind.includes('assignment')) return createElement(UserCheck, { size: 16 });
  if (kind.includes('enquir') || kind.includes('interest')) {
    return createElement(Handshake, { size: 16 });
  }
  return kind.includes('info')
    ? createElement(Info, { size: 16 })
    : createElement(Bell, { size: 16 });
}

export interface NotificationIconColors {
  ink: string;
  fill: string;
}

/** Icon chip ink + tinted background for one row, resolved from the type's own tone. */
export function accentColorsForType(
  type: string,
  colors: AppTheme['colors']
): NotificationIconColors {
  const kind = type.toLowerCase();
  if (kind.includes('document') || kind.includes('legal_opinion')) {
    return { ink: colors.accentText, fill: colors.primary[50] };
  }
  if (kind.includes('enquir') || kind.includes('interest') || kind.includes('assignment')) {
    return { ink: colors.semanticText.success, fill: colors.semantic.success[50] };
  }
  return { ink: colors.semanticText.info, fill: colors.semantic.info[50] };
}

export type NotificationBucket = 'today' | 'yesterday' | 'earlier';

export function bucketForDate(value: string): NotificationBucket {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'earlier';
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  return 'earlier';
}

export interface NotificationGroup {
  bucket: NotificationBucket;
  rows: NotificationRow[];
}

const BUCKET_ORDER: NotificationBucket[] = ['today', 'yesterday', 'earlier'];

export function groupByBucket(rows: NotificationRow[]): NotificationGroup[] {
  const buckets = new Map<NotificationBucket, NotificationRow[]>();
  for (const row of rows) {
    const bucket = bucketForDate(row.created_at);
    const existing = buckets.get(bucket);
    if (existing) existing.push(row);
    else buckets.set(bucket, [row]);
  }
  return BUCKET_ORDER.filter((bucket) => buckets.has(bucket)).map((bucket) => ({
    bucket,
    rows: buckets.get(bucket) as NotificationRow[]
  }));
}

export function isNeedsYouRow(row: NotificationRow): boolean {
  return row.needs_you && !row.is_read;
}
